import { Plus, Trash2 } from 'lucide-react'
import type { UrgentTier } from '@/types'
import Button from '@/components/ui/Button'
import { Input } from '@/components/ui/Field'

/**
 * One urgent row in an editor, all input strings: ready within `max_days`
 * working days (blank = any), from `min_qty` to `max_qty` pcs (blank = and
 * above), RM `price` per piece (blank = a free rush).
 */
export interface UrgentRowDraft {
  max_days: string
  min_qty: string
  max_qty: string
  price: string
}

const isWholeNumber = (raw: string) => /^\d+$/.test(raw.trim()) && Number(raw) >= 1

export function urgentRowDrafts(rows: UrgentTier[] | undefined): UrgentRowDraft[] {
  return (rows ?? []).map((row) => ({
    max_days: row.max_days == null ? '' : String(row.max_days),
    min_qty: String(row.min_qty),
    max_qty: row.max_qty == null ? '' : String(row.max_qty),
    price: row.price > 0 ? String(row.price) : '',
  }))
}

export function urgentRowsPayload(rows: UrgentRowDraft[]): UrgentTier[] {
  return rows.map((row) => ({
    max_days: row.max_days.trim() === '' ? null : Number(row.max_days),
    min_qty: Number(row.min_qty),
    max_qty: row.max_qty.trim() === '' ? null : Number(row.max_qty),
    price: Number(row.price) || 0,
  }))
}

/**
 * What is wrong with the rows, in words, or null when every speed reads as one
 * unbroken quantity ladder from 1 pc. Term for term with UrgentLadder on the
 * server, which refuses the save; this only says it before Save is pressed.
 */
export function urgentRowsProblem(rows: UrgentRowDraft[]): string | null {
  if (rows.length === 0) return 'Add at least one row.'
  if (rows.some((row) => !isWholeNumber(row.min_qty) || (row.max_qty.trim() !== '' && !isWholeNumber(row.max_qty)))) {
    return 'Quantities must be whole numbers from 1.'
  }
  if (rows.some((row) => row.max_days.trim() !== '' && !/^\d+$/.test(row.max_days.trim()))) {
    return 'Days must be whole working days, or blank for any.'
  }
  if (rows.some((row) => row.price.trim() !== '' && (!Number.isFinite(Number(row.price)) || Number(row.price) < 0))) {
    return 'Urgent prices cannot be negative.'
  }

  const speeds = new Map<string, UrgentRowDraft[]>()
  for (const row of rows) {
    const key = row.max_days.trim()
    speeds.set(key, [...(speeds.get(key) ?? []), row])
  }

  for (const [days, group] of speeds) {
    const label = days === '' ? 'Any speed' : `Within ${days} days`
    const sorted = [...group].sort((a, b) => Number(a.min_qty) - Number(b.min_qty))
    if (Number(sorted[0].min_qty) !== 1) return `${label}: the first range must start from 1 pc.`
    let previousMax: number | null = null
    for (const [index, row] of sorted.entries()) {
      const min = Number(row.min_qty)
      const max = row.max_qty.trim() === '' ? null : Number(row.max_qty)
      if (max != null && max < min) return `${label}: ${min} - ${max} pcs, "to" is less than "from".`
      if (index > 0 && previousMax == null) return `${label}: only the last range can be left open ("and above").`
      if (index > 0 && previousMax != null && min !== previousMax + 1) {
        return min <= previousMax
          ? `${label}: ranges overlap around ${min} pcs.`
          : `${label}: nothing covers ${previousMax + 1} - ${min - 1} pcs. Start the next range at ${previousMax + 1}.`
      }
      previousMax = max
    }
  }
  return null
}

/**
 * The urgent price table (client 2026-10-06, "makin cepat makin mahal"): rows
 * of ready within N working days / from / to / RM per piece. The tightest
 * speed that covers an order's working days applies, and every piece pays the
 * rate its product's total quantity falls in. Used for the standard table on
 * the SLA settings page and for a product's own prices.
 */
export default function UrgentRatesEditor({
  rows,
  onChange,
  allowEmpty = false,
}: {
  rows: UrgentRowDraft[]
  onChange: (rows: UrgentRowDraft[]) => void
  /** The standard table may be empty (no urgent service by default); a product's own prices may not. */
  allowEmpty?: boolean
}) {
  const problem = allowEmpty && rows.length === 0 ? null : urgentRowsProblem(rows)
  const updateRow = (index: number, patch: Partial<UrgentRowDraft>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  const addRow = () => {
    // Continue the last row's speed, right after its last bounded range.
    const last = rows.at(-1)
    const sameSpeed = rows.filter((row) => row.max_days === (last?.max_days ?? ''))
    const lastMax = [...sameSpeed].reverse().find((row) => isWholeNumber(row.max_qty))?.max_qty
    onChange([...rows, { max_days: last?.max_days ?? '', min_qty: lastMax ? String(Number(lastMax) + 1) : '1', max_qty: '', price: '' }])
  }

  return (
    <div className="basis-full rounded-lg border border-line p-2.5">
      {/* One line per row from sm up; on phones each row is two by two with
          its own small labels, since four inputs in a line read "ar" for
          "and above". */}
      <div className="hidden grid-cols-[1fr_1fr_1fr_1fr_auto] items-center gap-2 text-2xs font-semibold uppercase tracking-wide text-muted sm:grid">
        <span>Within (days)</span>
        <span>From pcs</span>
        <span>To pcs</span>
        <span>RM / pc</span>
        <span className="size-8" />
      </div>
      <div className="mt-1 space-y-3 sm:space-y-2">
        {rows.map((row, index) => (
          <div
            key={index}
            className="grid grid-cols-2 items-end gap-2 border-b border-line pb-3 last:border-b-0 sm:grid-cols-[1fr_1fr_1fr_1fr_auto] sm:items-center sm:border-b-0 sm:pb-0"
          >
            <label className="block">
              <span className="mb-0.5 block text-2xs font-semibold uppercase text-muted sm:hidden">Within (days)</span>
              <Input
                type="number"
                min={0}
                step={1}
                placeholder="any"
                aria-label="Ready within working days"
                value={row.max_days}
                onChange={(e) => updateRow(index, { max_days: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-0.5 block text-2xs font-semibold uppercase text-muted sm:hidden">From pcs</span>
              <Input
                type="number"
                min={1}
                step={1}
                aria-label="From pieces"
                value={row.min_qty}
                onChange={(e) => updateRow(index, { min_qty: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-0.5 block text-2xs font-semibold uppercase text-muted sm:hidden">To pcs</span>
              <Input
                type="number"
                min={1}
                step={1}
                placeholder="and above"
                aria-label="To pieces"
                value={row.max_qty}
                onChange={(e) => updateRow(index, { max_qty: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-0.5 block text-2xs font-semibold uppercase text-muted sm:hidden">RM / pc</span>
              <Input
                type="number"
                min={0}
                step="0.5"
                placeholder="0.00"
                aria-label="RM per piece"
                value={row.price}
                onChange={(e) => updateRow(index, { price: e.target.value })}
              />
            </label>
            <button
              type="button"
              title="Remove row"
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
              className="col-span-2 flex items-center justify-center gap-1.5 rounded-lg border border-line p-2 text-xs text-muted transition hover:text-danger sm:col-span-1"
            >
              <Trash2 className="size-4" />
              <span className="sm:hidden">Remove row</span>
            </button>
          </div>
        ))}
      </div>
      <Button variant="ghost" className="mt-2" onClick={addRow}>
        <Plus className="size-4" /> Add row
      </Button>
      {problem ? (
        <p className="mt-1.5 text-2xs text-danger">{problem}</p>
      ) : (
        <p className="mt-1.5 text-2xs text-muted">
          RM per piece on top of the tier price, for orders whose delivery date is shorter than normal. "Within" is how many
          working days the order has from production start to its delivery date: the tightest row that still covers it
          applies (blank = any speed), and slower orders take the slowest row. The whole quantity pays the rate of the
          range it falls in. Leave "to" blank on a speed's last range for "and above". A blank price is a free rush.
        </p>
      )}
    </div>
  )
}
